// ─── Marca del CRM (Booster) ──────────────────────────────────────────────────
// El CRM (login principal + app logueada) usa la marca "Booster". El sitio
// PÚBLICO (home, propiedades, portal del propietario, subastas, etc.) NO cambia:
// queda siempre Imperium dorado. Eso se garantiza por construcción — el theme
// global es Imperium y SOLO las superficies del CRM se envuelven con esta marca
// (ver CrmThemeProvider + router).
//
// Para fijar el color definitivo del CRM, cambiar CRM_BRAND:
//   'booster'      → verde Phylax (emerald)
//   'booster-blue' → azul (variante)
//   'imperium'     → dorado (volver atrás)
// El parámetro ?brand=... en la URL permite comparar las variantes sin recompilar,
// pero NO se persiste (antes se guardaba en localStorage y dejaba la marca pineada).
import { IMPERIUM_PALETTE, BOOSTER_PALETTE, BOOSTER_BLUE_PALETTE } from './palette';
import { withAccent } from './withAccent';
import { conmemoracionDeHoy, DATOS_CONMEMORACION, CELESTE_URUGUAY } from './conmemoracion';

export type BrandId = 'imperium' | 'booster' | 'booster-blue';

/** Marca configurada para el CRM (cambiar acá para fijar el color definitivo). */
export const CRM_BRAND: BrandId = 'booster';

const VALID: BrandId[] = ['imperium', 'booster', 'booster-blue'];

/** Marca activa del CRM: override efímero por ?brand=... o el default CRM_BRAND.
 *  Importante: NO leemos localStorage — la marca la manda CRM_BRAND, así no queda
 *  una variante vieja pineada en el navegador. */
export function getCrmBrand(): BrandId {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('brand') as BrandId | null;
    if (fromUrl && VALID.includes(fromUrl)) return fromUrl;
  } catch {
    /* ignore */
  }
  return CRM_BRAND;
}

export function getBrandPalette(brand: BrandId) {
  if (brand === 'booster') return BOOSTER_PALETTE;
  if (brand === 'booster-blue') return BOOSTER_BLUE_PALETTE;
  return IMPERIUM_PALETTE;
}

/** Paleta de la marca activa del CRM. */
export function getCrmPalette() {
  return getBrandPalette(getCrmBrand());
}

// ─── White-label: color del tenant aplicado a TODA la marca ───────────────────
// CRM_PALETTE (brandPalette.ts) y getCrmPalette() devuelven el MISMO objeto
// BOOSTER_PALETTE. Lo mutamos IN PLACE con el color del tenant → los ~200
// componentes que importan CRM_PALETTE directo ven el color, no solo el tema MUI.
// Snapshot pristino del acento base para derivar siempre desde el original (sin drift).
// EXPORTADO: las superficies PÚBLICAS (booster landing) deben leer la marca Booster de acá,
// NO de CRM_PALETTE — esa se muta in-place por el white-label del tenant y filtraría su color.
export const BOOSTER_BASE = {
  brand:     { ...BOOSTER_PALETTE.brand },
  gradients: { ...BOOSTER_PALETTE.gradients },
  border:    { ...BOOSTER_PALETTE.border },
  shadow:    { ...BOOSTER_PALETTE.shadow },
};
let appliedAccent: string | undefined;
/** ¿El acento activo es el celeste de la conmemoración? Lo decide `applyCrmAccent`. */
let conmemoraActiva = false;
// Dorado BASE del acento activo (el tono "claro" del tenant/Booster). applyCrmMode lo usa
// para restaurar brand.golden en modo oscuro. Default = dorado Booster pristino.
let accentBaseGolden: string = BOOSTER_PALETTE.brand.golden;

const ACCENT_KEY = 'crm-accent';
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Muta la paleta Booster con el color del tenant (o restaura si no hay). Idempotente. */
export function applyCrmAccent(
  color?: string,
  /**
   * Solo para los tests: hoy la conmemoración está apagada por `CONMEMORACION_ACTIVA`, y sin
   * poder inyectarla no habría forma de seguir probando que el viraje al celeste funciona
   * cuando se vuelva a encender. En la app NADIE lo pasa — se resuelve solo adentro.
   */
  hayConmemoracion?: boolean,
) {
  // El 25 de Agosto el acento de Imperium entra celeste — ver `acentoConmemorativo`.
  const efectivo = hayConmemoracion === undefined
    ? acentoConmemorativo(color)
    : acentoConmemorativo(color, hayConmemoracion);
  // Se deduce del VIRAJE: si el color entró como el dorado de Imperium y sale celeste, hay
  // conmemoración. Por eso `color` tiene que ser el color REAL del tenant y nunca uno ya
  // virado (ver la nota en CrmThemeProvider).
  //
  // Se anota ANTES del guard de idempotencia: si el color no cambió se sale sin llegar al
  // final, y la bandera quedaría sin actualizar.
  conmemoraActiva = !!color && efectivo !== color;
  if (efectivo === appliedAccent) return;
  appliedAccent = efectivo;
  const d = withAccent(BOOSTER_BASE as typeof BOOSTER_PALETTE, efectivo);
  Object.assign(BOOSTER_PALETTE.brand,     d.brand);
  Object.assign(BOOSTER_PALETTE.gradients, d.gradients);
  Object.assign(BOOSTER_PALETTE.border,    d.border);
  Object.assign(BOOSTER_PALETTE.shadow,    d.shadow);
  accentBaseGolden = d.brand.golden; // dorado base de este acento (para restaurar en oscuro)
}

// ─── Modo claro/oscuro: tokens de SUPERFICIE de la marca CRM ──────────────────
// Los ~200 componentes que leen CRM_PALETTE.bg/.text/.border/.shadow directo (no vía
// el tema MUI) quedaban SIEMPRE oscuros. applyCrmMode reasigna esas props al modo activo
// → esos componentes se adaptan sin tocarlos uno por uno.
// CLAVE: bg/text NO se re-spreadean en BOOSTER_PALETTE (comparten referencia con
// IMPERIUM_PALETTE, la paleta del sitio PÚBLICO). Por eso REASIGNAMOS la prop con un
// objeto nuevo — nunca mutamos el objeto anidado in-place — para no arrastrar el público a claro.
const CRM_DARK_BG     = { ...IMPERIUM_PALETTE.bg };
const CRM_DARK_TEXT   = { ...IMPERIUM_PALETTE.text };
const CRM_DARK_BORDER = { subtle: IMPERIUM_PALETTE.border.subtle, default: IMPERIUM_PALETTE.border.default, strong: IMPERIUM_PALETTE.border.strong };
const CRM_DARK_SHADOW = { sm: IMPERIUM_PALETTE.shadow.sm, md: IMPERIUM_PALETTE.shadow.md, lg: IMPERIUM_PALETTE.shadow.lg, xl: IMPERIUM_PALETTE.shadow.xl };

// Tokens claros (los mismos que deriva buildTheme en modo light).
const CRM_LIGHT_BG     = { base: '#f0f2f5', paper: '#ffffff', elevated: '#fafafa', overlay: '#eef1f4', glass: 'rgba(255,255,255,0.92)' };
const CRM_LIGHT_TEXT   = { primary: '#1c2128', secondary: '#57606a', disabled: '#8c959f', inverse: '#ffffff' };
const CRM_LIGHT_BORDER = { subtle: 'rgba(0,0,0,0.08)', default: 'rgba(0,0,0,0.12)', strong: 'rgba(0,0,0,0.20)' };
const CRM_LIGHT_SHADOW = {
  sm: '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
  md: '0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.05)',
  lg: '0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.04)',
  xl: '0 20px 25px rgba(0,0,0,0.06), 0 10px 10px rgba(0,0,0,0.03)',
};

let appliedMode: 'light' | 'dark' | undefined;

/** Ajusta los tokens de superficie (bg/text/border/shadow) de la marca CRM al modo.
 *  Se llama desde CrmThemeProvider en cada render (idempotente). Preserva las claves de
 *  acento (border.golden, shadow.glow) que administra applyCrmAccent. */
export function applyCrmMode(mode: 'light' | 'dark') {
  const P = getBrandPalette(getCrmBrand()) as typeof BOOSTER_PALETTE;
  // Si el CRM comparte paleta con el sitio público (marca 'imperium'), NO tocar: mutaría el público.
  if ((P as unknown) === (IMPERIUM_PALETTE as unknown)) return;
  const light = mode === 'light';
  // DORADO de TEXTO/ÍCONOS: en CLARO usamos el tono oscuro (goldenDark) para mejor contraste
  // sobre fondo claro; en OSCURO volvemos al dorado base. Se aplica SIEMPRE (antes del guard)
  // para quedar correcto incluso tras un cambio de acento (que resetea brand.golden al base).
  // Los componentes que hornean `const golden = P.brand.golden` capturan el valor correcto en el
  // próximo load (applyCrmMode corre antes de que los chunks lazy evalúen). goldenMid/goldenLight
  // NO se tocan → gradientes y el `golden` del tema MUI (usa goldenMid en claro) quedan intactos.
  P.brand.golden = light ? P.brand.goldenDark : accentBaseGolden;
  if (mode === appliedMode) return;
  appliedMode = mode;
  P.bg     = { ...(light ? CRM_LIGHT_BG : CRM_DARK_BG) };
  P.text   = { ...(light ? CRM_LIGHT_TEXT : CRM_DARK_TEXT) };
  P.border = { ...P.border, ...(light ? CRM_LIGHT_BORDER : CRM_DARK_BORDER) }; // conserva border.golden
  P.shadow = { ...P.shadow, ...(light ? CRM_LIGHT_SHADOW : CRM_DARK_SHADOW) }; // conserva shadow.glow
}

/** Persiste el color para aplicarlo en el PRÓXIMO load antes de que los componentes capturen la paleta. */
export function persistCrmAccent(color?: string) {
  try { if (color && HEX_RE.test(color)) localStorage.setItem(ACCENT_KEY, color); else localStorage.removeItem(ACCENT_KEY); } catch { /* noop */ }
}

function readStoredAccent(): string | undefined {
  try { const v = localStorage.getItem(ACCENT_KEY); return v && HEX_RE.test(v) ? v : undefined; } catch { return undefined; }
}

// ─── Acento por DOMINIO (pre-login) ───────────────────────────────────────────
// Antes de loguearse no hay branding del tenant, así que el CRM caería al emerald
// Booster por defecto. Pero el /login servido desde un dominio de Imperium debe verse
// dorado (su marca). Resolvemos el acento por host: dominios de Imperium → dorado nativo,
// el resto (boostercrm.app, etc.) → undefined = marca Booster. Es el MISMO color que usa
// el CRM logueado de Imperium (NATIVE_BRANDING.primaryColor del backend).
export const IMPERIUM_NATIVE_ACCENT = '#D8BF82';

/**
 * CONMEMORACIÓN — el 25 de Agosto el acento de Imperium ES el celeste. Todo el CRM, de una vez.
 *
 * (25/08/2026, pedido del user) Acá está el único lugar donde se decide, y por eso el efecto
 * alcanza a TODO: `applyCrmAccent` deriva la paleta entera desde este color con `withAccent` y
 * muta `CRM_PALETTE` in-place, así que los ~200 componentes que la leen directo —más el tema de
 * MUI y las variables CSS `--brand-*` del provider— pasan a celeste sin tocar ninguno.
 *
 * Antes esto se venía pintando componente por componente (sidebar, chips, topbar, banner). Esa
 * forma no escala a la pantalla entera: cada dorado que no estuviera en la lista quedaba dorado
 * en medio del celeste, y además el tono se iba separando —el celeste a mano y el que deriva
 * `withAccent` no son el mismo color—.
 *
 * ⛔ SOLO IMPERIUM, y por eso mira el acento y no la fecha a secas: la red es MULTI-PAÍS. Se
 * reconoce al tenant porque su acento ES el dorado nativo de Imperium (el mismo que sirve el
 * backend en `NATIVE_BRANDING.primaryColor`, y el que da `accentFromHost()` en el login antes
 * de que haya branding). Teñir de celeste la marca de una inmobiliaria que no es uruguaya sería
 * pintarle su identidad por una fecha que no es suya.
 *
 * ⛔ NO se persiste: `persistCrmAccent` guarda el color REAL del tenant (ver CrmThemeProvider).
 * Guardar el celeste lo dejaría pineado el 26, aplicado al cargar el módulo, antes de que nadie
 * pueda corregirlo.
 */
export function acentoConmemorativo(
  color?: string,
  /**
   * Entra como parámetro por el mismo motivo que `enDesarrollo` en `conmemoracionDeHoy`:
   * en desarrollo la conmemoración está SIEMPRE encendida —y los tests corren con
   * `DEV === true`—, así que sin poder inyectarlo no habría forma de probar el día en que
   * está apagada, que es la mitad del comportamiento.
   */
  hayConmemoracion: boolean = !!conmemoracionDeHoy(),
): string | undefined {
  if (!color || color.toLowerCase() !== IMPERIUM_NATIVE_ACCENT.toLowerCase()) return color;
  return hayConmemoracion ? CELESTE_URUGUAY : color;
}

/**
 * La conmemoración TAL COMO APLICA A ESTE CRM — el único predicado que deben preguntar
 * los componentes de adentro del CRM.
 *
 * El color ya viaja solo por el acento y no hace falta pedirlo; esto existe para lo que
 * NO es color: la banderita, el tooltip que explica la fecha, el panel de la declaratoria
 * en la home. Devuelve `null` —y no la conmemoración del calendario— cuando el CRM que se
 * está mirando no es el de Imperium.
 *
 * ⛔ USAR ESTO Y NO `conmemoracionDeHoy()` DENTRO DEL CRM. `conmemoracionDeHoy()` solo
 * mira la fecha: preguntándole directo, una inmobiliaria de otro país se llevaba la
 * banderita uruguaya y el acento celeste sin haber pedido ninguna de las dos cosas.
 */
/**
 * El acento que quedó REALMENTE aplicado (ya con el celeste del 25 si correspondía).
 *
 * Existe para que nadie tenga que volver a preguntarse si hoy va celeste: se llama a
 * `applyCrmAccent` con el color del tenant y se lee de acá lo que salió. Resolver el celeste
 * por segunda vez afuera es justo lo que rompió la conmemoración una vez.
 */
export function acentoAplicado(): string | undefined {
  return appliedAccent;
}

export function conmemoracionCrm() {
  // ⛔ NO se vuelve a preguntar la fecha acá: se devuelve el contenido tal cual. Lo único que
  // decide es `conmemoraActiva`, que salió del viraje REAL del acento en `applyCrmAccent`.
  // Resolverlo por segunda vez es lo que dejó la pantalla celeste sin banderita aquella vez.
  return conmemoraActiva ? DATOS_CONMEMORACION : null;
}
// `imperium.uy` estaba acá y NO existe: nadie puede ser servido desde ese host, así que
// la entrada nunca matcheaba. El sitio es inmobiliariaimperium.com.
const IMPERIUM_HOSTS = ['inmobiliariaimperium.com'];
/** ¿La app se está sirviendo desde el dominio de Imperium? (boostercrm.app, localhost → no) */
export function esHostImperium(): boolean {
  try {
    const host = window.location.hostname.toLowerCase();
    return IMPERIUM_HOSTS.some((h) => host === h || host.endsWith('.' + h));
  } catch { return false; }
}
export function accentFromHost(): string | undefined {
  try {
    const host = window.location.hostname.toLowerCase();
    if (IMPERIUM_HOSTS.some((h) => host === h || host.endsWith('.' + h))) return IMPERIUM_NATIVE_ACCENT;
  } catch { /* sin window */ }
  return undefined;
}

// CLAVE: aplicar el color guardado AL CARGAR ESTE MÓDULO — antes de que los
// componentes evalúen `const golden = CRM_PALETTE.brand.golden` (capturas a nivel
// módulo). Así el color del tenant entra en TODO, no solo en lo que se lee en render.
applyCrmAccent(readStoredAccent());

// MISMO motivo para el MODO claro/oscuro: aplicarlo al cargar el módulo para que el dorado
// de texto/íconos (goldenDark en claro) quede horneado en los `const golden` de módulo, y no
// solo en lo que se lee en render. CrmThemeProvider lo reaplica en cada render (idempotente).
function readStoredMode(): 'light' | 'dark' {
  try {
    const m = localStorage.getItem('imperium_theme'); // misma key que AppShell/ThemeModeProvider
    if (m === 'light' || m === 'dark') return m;
  } catch { /* sin storage */ }
  return 'dark';
}
applyCrmMode(readStoredMode());
